import type {
  ClassifyRequest,
  ClassifyResponse,
  TaxEstimateRequest,
  TaxEstimateResponse,
  ParsedInvoice,
  ApiError,
  ApiResponse,
  Customer,
  Consignment,
  ConsignmentDetail,
  CreateConsignmentRequest,
  CreateConsignmentResponse,
  CreateCommunicationRequest,
  UpdateConsignmentItemRequest,
  UpdateConsignmentRequest,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  Profile,
  HelmStaffInvitation,
  HelmUser,
  HelmUsersResponse,
  HelmCommunication,
  ListCommunicationsRequest,
  AttachConsignmentDocumentRequest,
  ConsignmentDocument,
  ConsignmentDocumentType,
  GenerateConsignmentDocumentRequest,
  ConsignmentItem,
  ConsignmentTrackingEvent,
  CreateTrackingEventRequest,
  SeaTrackingContainersResponse,
  SeaTrackingImportRequest,
  SeaTrackingImportResponse,
  SeaTrackingShipment,
  RecalculateConsignmentTaxResponse,
  StorageUploadInitRequest,
  StorageUploadInitResponse,
  CreateHelmInvitationRequest,
  UpdateHelmUserRolesRequest,
} from './types'

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4003'

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`

    const config: RequestInit = {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    }

    try {
      const response = await fetch(url, config)

      if (!response.ok) {
        const error: ApiError = await response.json()
        throw new Error(error.error?.message || `HTTP ${response.status}: ${response.statusText}`)
      }

      const json: ApiResponse<T> = await response.json()
      return json.data
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('An unknown error occurred')
    }
  }

  /**
   * Classify an item to get HS code using AI
   */
  async classify(data: ClassifyRequest): Promise<ClassifyResponse> {
    return this.request<ClassifyResponse>('/classify', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Estimate taxes for a consignment
   */
  async estimateTax(data: TaxEstimateRequest): Promise<TaxEstimateResponse> {
    return this.request<TaxEstimateResponse>('/tax/estimate', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Parse invoice to extract items
   */
  async parseInvoice(imageUrl: string): Promise<ParsedInvoice> {
    return this.request<ParsedInvoice>('/parse-invoice', {
      method: 'POST',
      body: JSON.stringify({ image_url: imageUrl }),
    })
  }

  /**
   * Create a new consignment
   */
  async createConsignment(data: CreateConsignmentRequest): Promise<CreateConsignmentResponse> {
    return this.request<CreateConsignmentResponse>('/consignments', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async listConsignments(): Promise<Consignment[]> {
    return this.request<Consignment[]>('/consignments')
  }

  async getConsignment(id: string): Promise<ConsignmentDetail> {
    return this.request<ConsignmentDetail>(`/consignments/${id}`)
  }

  async updateConsignment(id: string, data: UpdateConsignmentRequest): Promise<Consignment> {
    return this.request<Consignment>(`/consignments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteConsignment(id: string): Promise<{ deleted: true }> {
    return this.request<{ deleted: true }>(`/consignments/${id}`, {
      method: 'DELETE',
    })
  }

  async updateConsignmentItem(
    consignmentId: string,
    itemId: string,
    data: UpdateConsignmentItemRequest,
  ): Promise<ConsignmentItem> {
    return this.request<ConsignmentItem>(`/consignments/${consignmentId}/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async recalculateConsignmentTaxes(
    consignmentId: string,
    data: { currency?: string; target_currency?: string } = {},
  ): Promise<RecalculateConsignmentTaxResponse> {
    return this.request<RecalculateConsignmentTaxResponse>(`/consignments/${consignmentId}/tax-estimates/recalculate`, {
      method: 'POST',
      body: JSON.stringify({
        currency: data.currency ?? 'USD',
        target_currency: data.target_currency ?? 'UGX',
      }),
    })
  }

  async generateConsignmentDocument(
    consignmentId: string,
    data: ConsignmentDocumentType | GenerateConsignmentDocumentRequest,
  ): Promise<ConsignmentDocument> {
    return this.request<ConsignmentDocument>(`/consignments/${consignmentId}/documents/generate`, {
      method: 'POST',
      body: JSON.stringify(typeof data === 'string' ? { type: data } : data),
    })
  }

  async attachConsignmentDocument(
    consignmentId: string,
    data: AttachConsignmentDocumentRequest,
  ): Promise<ConsignmentDocument> {
    return this.request<ConsignmentDocument>(`/consignments/${consignmentId}/documents`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async deleteConsignmentDocument(id: string): Promise<{ deleted: true }> {
    return this.request<{ deleted: true }>(`/documents/${id}`, {
      method: 'DELETE',
    })
  }

  getDocumentDownloadUrl(id: string): string {
    return `${this.baseUrl}/documents/${id}/download`
  }

  async createStorageUpload(data: StorageUploadInitRequest): Promise<StorageUploadInitResponse> {
    return this.request<StorageUploadInitResponse>('/storage/uploads', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async uploadToSignedUrl(upload: StorageUploadInitResponse, file: File): Promise<void> {
    const response = await fetch(upload.upload_url, {
      method: upload.upload_method,
      headers: upload.upload_headers,
      body: file,
    })

    if (!response.ok) {
      throw new Error(`Upload failed with HTTP ${response.status}`)
    }
  }

  async recordConsignmentTrackingEvent(
    consignmentId: string,
    data: CreateTrackingEventRequest,
  ): Promise<ConsignmentTrackingEvent> {
    return this.request<ConsignmentTrackingEvent>(`/consignments/${consignmentId}/tracking-events`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async importSeaTrackingShipment(data: SeaTrackingImportRequest): Promise<SeaTrackingImportResponse> {
    return this.request<SeaTrackingImportResponse>('/tracking/sea/shipments/imports', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getSeaTrackingShipment(reference: string): Promise<SeaTrackingShipment> {
    const query = new URLSearchParams({ reference })
    return this.request<SeaTrackingShipment>(`/tracking/sea/shipments/by-reference?${query.toString()}`)
  }

  async listSeaTrackingContainers(
    reference: string,
    page = 1,
    pageSize = 100,
  ): Promise<SeaTrackingContainersResponse> {
    const query = new URLSearchParams({
      reference,
      page: String(page),
      page_size: String(pageSize),
    })
    return this.request<SeaTrackingContainersResponse>(
      `/tracking/sea/shipments/by-reference/containers?${query.toString()}`,
    )
  }

  async listCommunications(params: ListCommunicationsRequest): Promise<HelmCommunication[]> {
    const query = new URLSearchParams()
    if (params.customer_id) query.set('customer_id', params.customer_id)
    if (params.consignment_id) query.set('consignment_id', params.consignment_id)
    if (params.limit) query.set('limit', String(params.limit))
    const suffix = query.toString() ? `?${query.toString()}` : ''

    return this.request<HelmCommunication[]>(`/communications${suffix}`)
  }

  async createCommunication(data: CreateCommunicationRequest): Promise<HelmCommunication> {
    return this.request<HelmCommunication>('/communications', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async listCustomers(includeArchived = false): Promise<Customer[]> {
    return this.request<Customer[]>(`/customers?include_archived=${includeArchived ? 'true' : 'false'}`)
  }

  async getCustomer(id: string): Promise<Customer> {
    return this.request<Customer>(`/customers/${id}`)
  }

  async createCustomer(data: CreateCustomerRequest): Promise<Customer> {
    return this.request<Customer>('/customers', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateCustomer(id: string, data: UpdateCustomerRequest): Promise<Customer> {
    return this.request<Customer>(`/customers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async archiveCustomer(id: string): Promise<Customer> {
    return this.request<Customer>(`/customers/${id}/archive`, {
      method: 'POST',
    })
  }

  async restoreCustomer(id: string): Promise<Customer> {
    return this.request<Customer>(`/customers/${id}/restore`, {
      method: 'POST',
    })
  }

  async deleteCustomer(id: string): Promise<{ deleted: true }> {
    return this.request<{ deleted: true }>(`/customers/${id}`, {
      method: 'DELETE',
    })
  }

  async listHelmUsers(): Promise<HelmUsersResponse> {
    return this.request<HelmUsersResponse>('/helm/users')
  }

  async inviteHelmUser(data: CreateHelmInvitationRequest): Promise<HelmStaffInvitation> {
    return this.request<HelmStaffInvitation>('/helm/invitations', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async acceptHelmInvitation(token: string): Promise<HelmStaffInvitation> {
    return this.request<HelmStaffInvitation>('/helm/invitations/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
  }

  async revokeHelmInvitation(id: string): Promise<HelmStaffInvitation> {
    return this.request<HelmStaffInvitation>(`/helm/invitations/${id}/revoke`, {
      method: 'POST',
    })
  }

  async updateHelmUserRoles(id: string, data: UpdateHelmUserRolesRequest): Promise<HelmUser> {
    return this.request<HelmUser>(`/helm/users/${id}/roles`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async getProfile(): Promise<Profile> {
    return this.request<Profile>('/profiles/me')
  }
}

// Export singleton instance
export const apiClient = new ApiClient()

// Export class for testing
export { ApiClient }
